import {
  IPracticeSetupStep,
  IPracticeSetupWizard,
  PracticeSetupStepStatus,
  PracticeSetupWizardStatus,
} from '@principle-theorem/principle-core/interfaces';
import {
  asReffable,
  asyncForEach,
  CollectionReference,
  DocumentReference,
  Firestore,
  Region,
  toTimestamp,
  undeletedFilter,
  WithRef,
} from '@principle-theorem/shared';
import { IPracticeSetupStepDefinition } from './practice-setup-steps/practice-setup-step.types';
import { PracticeSetupWizard } from './practice-setup-wizard';

export class PracticeSetupWizardStepResync {
  private readonly _validStepIds: Set<string>;
  private readonly _definitionMap: Map<string, IPracticeSetupStepDefinition>;

  private constructor(
    private readonly _wizardRef: DocumentReference<IPracticeSetupWizard>,
    private readonly _wizardDoc: WithRef<IPracticeSetupWizard>,
    private _existingSteps: WithRef<IPracticeSetupStep>[],
    private readonly _regionStepDefinitions: IPracticeSetupStepDefinition[]
  ) {
    this._validStepIds = new Set(
      this._regionStepDefinitions.map(({ id }) => id)
    );
    this._definitionMap = new Map(
      this._regionStepDefinitions.map((def) => [def.id, def])
    );
  }

  static async run(
    wizardRef: DocumentReference<IPracticeSetupWizard>,
    region: Region
  ): Promise<{ hasChanges: boolean }> {
    const wizardDoc = await Firestore.getDoc(wizardRef);
    const existingSteps = await Firestore.getDocs(
      PracticeSetupWizard.stepCol(asReffable(wizardRef))
    );
    const regionStepDefinitions =
      PracticeSetupWizard.getRegionFilteredSteps(region);

    const resync = new PracticeSetupWizardStepResync(
      wizardRef,
      wizardDoc,
      existingSteps.filter(undeletedFilter),
      regionStepDefinitions
    );

    const removedOrphans = await resync._removeOrphanedSteps();
    const updatedMetadata = await resync._updateStaleMetadata();
    const addedMissing = await resync._addMissingSteps();
    const hasStructuralChanges =
      removedOrphans || updatedMetadata || addedMissing;

    const statusChanged = await resync._recomputeWizardStatus();
    return { hasChanges: hasStructuralChanges || statusChanged };
  }

  private _stepCol(): CollectionReference<IPracticeSetupStep> {
    return PracticeSetupWizard.stepCol(asReffable(this._wizardRef));
  }

  private async _removeOrphanedSteps(): Promise<boolean> {
    const orphans = this._existingSteps.filter(
      (step) => !this._validStepIds.has(step.id)
    );
    for (const step of orphans) {
      await Firestore.deleteDoc(step.ref);
    }
    return orphans.length > 0;
  }

  private async _updateStaleMetadata(): Promise<boolean> {
    let changed = false;
    const retainedSteps = this._existingSteps.filter((step) =>
      this._validStepIds.has(step.id)
    );

    await asyncForEach(retainedSteps, async (step) => {
      const def = this._definitionMap.get(step.id);
      if (!def) {
        return;
      }
      if (
        step.title !== def.title ||
        step.description !== def.description ||
        step.category !== def.category
      ) {
        await Firestore.patchDoc(step.ref, {
          title: def.title,
          description: def.description,
          category: def.category,
        });
        changed = true;
      }
    });

    return changed;
  }

  private async _addMissingSteps(): Promise<boolean> {
    const existingIds = new Set(this._existingSteps.map((step) => step.id));
    const missingDefs = this._regionStepDefinitions.filter(
      (def) => !existingIds.has(def.id)
    );

    await asyncForEach(missingDefs, (def) =>
      Firestore.addDoc(
        this._stepCol(),
        {
          id: def.id,
          category: def.category,
          title: def.title,
          description: def.description,
          status: PracticeSetupStepStatus.NotStarted,
          validationPassed: false,
          deleted: false,
        },
        def.id
      )
    );

    return missingDefs.length > 0;
  }

  private async _recomputeWizardStatus(): Promise<boolean> {
    const allSteps = await Firestore.getDocs(this._stepCol());
    const relevantSteps = allSteps
      .filter(undeletedFilter)
      .filter((step) => this._validStepIds.has(step.id));
    const allComplete = relevantSteps.every(
      (step) => step.status === PracticeSetupStepStatus.Complete
    );
    const someStarted = relevantSteps.some((step) =>
      [
        PracticeSetupStepStatus.InProgress,
        PracticeSetupStepStatus.Complete,
      ].includes(step.status)
    );

    let newStatus = this._wizardDoc.status;
    let completedAt = this._wizardDoc.completedAt;

    if (allComplete) {
      newStatus = PracticeSetupWizardStatus.Complete;
      completedAt = this._wizardDoc.completedAt ?? toTimestamp();
    } else if (someStarted) {
      newStatus = PracticeSetupWizardStatus.InProgress;
    } else {
      newStatus = PracticeSetupWizardStatus.NotStarted;
    }

    const statusChanged = newStatus !== this._wizardDoc.status;

    if (statusChanged) {
      await Firestore.updateDoc(this._wizardRef, {
        status: newStatus,
        completedAt,
      });
    }

    return statusChanged;
  }
}
